/**
 * PURE. Minimal forward kinematics for previews and tests (the full runtime is P2's `AnimActor`): a sparse pose of
 * Euler-degree rotations (YXZ, creatures §B1) and translations → model-space bone matrices → the skin palette
 * `world · boneModel · translate(−bindPivot)` (architecture §5.8 `ActorFrame.palette`). Also the posture rest layer
 * (creatures §B4) and a few lab idles.
 */
import { m4FromRTS, m4Mul, qFromEulerYXZ, type BoneKey, type Rig } from '../../shared';
import type { CreatureSpec } from '../dna/resolve';

export type SparsePose = Partial<Record<string, BoneKey>>;

const tmpQ = new Float32Array(4);
const tmpL = new Float32Array(16);
const tmpT = new Float32Array(16);
const ONE = new Float32Array([1, 1, 1]);

/** Adds pose `b` onto `a` (rotations add per axis — fine for the small layered angles of previews). */
export function addPose(a: SparsePose, b: SparsePose, w = 1): SparsePose {
  const out: SparsePose = { ...a };
  for (const [k, v] of Object.entries(b)) {
    if (!v) continue;
    const cur = out[k] ?? {};
    out[k] = {
      r: v.r ? [(cur.r?.[0] ?? 0) + v.r[0] * w, (cur.r?.[1] ?? 0) + v.r[1] * w, (cur.r?.[2] ?? 0) + v.r[2] * w] : cur.r,
      t: v.t ? [(cur.t?.[0] ?? 0) + v.t[0] * w, (cur.t?.[1] ?? 0) + v.t[1] * w, (cur.t?.[2] ?? 0) + v.t[2] * w] : cur.t,
    };
  }
  return out;
}

/** Fills `model` (boneCount × 16) and `palette` (boneCount × 16) for `pose` under `world` (column-major). */
export function poseToPalette(rig: Rig, pose: SparsePose, world: Float32Array, model: Float32Array, palette: Float32Array): void {
  const bones = rig.bones;
  for (let i = 0; i < bones.length; i++) {
    const b = bones[i];
    const k = pose[b.name];
    const par = b.parent;
    const px = b.pivot[0] - (par >= 0 ? bones[par].pivot[0] : 0);
    const py = b.pivot[1] - (par >= 0 ? bones[par].pivot[1] : 0);
    const pz = b.pivot[2] - (par >= 0 ? bones[par].pivot[2] : 0);
    const r = k?.r ?? [0, 0, 0];
    const t = k?.t ?? [0, 0, 0];
    qFromEulerYXZ(tmpQ, r[0], r[1], r[2]);
    m4FromRTS(tmpL, tmpQ, [px + t[0], py + t[1], pz + t[2]], ONE);
    const out = model.subarray(i * 16, i * 16 + 16);
    if (par >= 0) m4Mul(out, model.subarray(par * 16, par * 16 + 16), tmpL); else out.set(tmpL);
    // palette = world · model · T(−pivot)
    m4Mul(tmpT, world, out);
    const pal = palette.subarray(i * 16, i * 16 + 16);
    pal.set(tmpT);
    pal[12] = tmpT[12] - (tmpT[0] * b.pivot[0] + tmpT[4] * b.pivot[1] + tmpT[8] * b.pivot[2]);
    pal[13] = tmpT[13] - (tmpT[1] * b.pivot[0] + tmpT[5] * b.pivot[1] + tmpT[9] * b.pivot[2]);
    pal[14] = tmpT[14] - (tmpT[2] * b.pivot[0] + tmpT[6] * b.pivot[1] + tmpT[10] * b.pivot[2]);
  }
}

/** Creatures §B4 posture rest layer (additive; P2 weights it by state, §C2.2). */
export function postureLayer(spec: CreatureSpec): SparsePose {
  const { hunch, sway, headFwd } = spec.v;
  const haughty = spec.o.posture === 'haughty' ? -8 : 0;
  return {
    pelvis: { t: [0.04 * sway * 0.5, 0, 0], r: [0, 0, 5 * sway] },
    spine_02: { r: [18 * hunch - 4 * sway, 0, -8 * sway] },
    neck: { r: [10 * hunch + 15 * headFwd, 0, 0] },
    head: { r: [-12 * headFwd + haughty - 10 * hunch, 0, 6 * sway] },
  };
}

/** A gentle preview idle (breathing, arm hang, tail sway) at time `ms` with phase from the soul seed. */
export function previewIdle(spec: CreatureSpec, rig: Rig, ms: number): SparsePose {
  const ph = (spec.dna.soulSeed % 1000) / 1000 * Math.PI * 2;
  const t = ms / 1000;
  const br = Math.sin(t * 2 * Math.PI / 3.6 + ph);
  const pose: SparsePose = {
    spine_01: { r: [1.2 * br, 0, 0] }, spine_02: { r: [-1.5 * br, 0, 0] }, head: { r: [1.2 * br, 2.5 * Math.sin(t * 0.7 + ph), 0] },
    upperarm_L: { r: [3 * br, 0, 9] }, upperarm_R: { r: [3 * br, 0, -9] }, forearm_L: { r: [-12, 0, 0] }, forearm_R: { r: [-12, 0, 0] },
    hand_L: { r: [0, -10, 0] }, hand_R: { r: [0, 10, 0] },
  };
  for (const b of rig.bones) {
    const m = /^tail_(\d)$/.exec(b.name);
    if (m) { const i = Number(m[1]); pose[b.name] = { r: [2 * Math.sin(t * 1.3 + i * 0.6 + ph), 7 * Math.sin(t * 1.1 + i * 0.7 + ph), 0] }; }
    if (b.name === 'ear_L_01' || b.name === 'ear_R_01') {
      const tw = Math.max(0, Math.sin(t * 0.9 + (b.name === 'ear_L_01' ? 0 : 2) + ph) - 0.93) * 140;
      pose[b.name] = { r: [-tw, 0, 0] };
    }
  }
  return pose;
}
